import React from 'react';
import { BodySize, BaseSize, TextWeight, TextColor } from '@/shared/types/text';
import { cn } from '@/shared/utils/cn';
import { SYSTEM_COLORS } from './constants';

export interface BodyProps {
  /** Tamaño del body: xs, sm, md, lg o tamaños base */
  size?: BodySize | BaseSize;
  /** Peso de la fuente */
  weight?: TextWeight;
  /** Contenido del texto */
  children: React.ReactNode;
  /** Clase CSS adicional */
  className?: string;
  /** Elemento HTML a renderizar (por defecto: span) */
  as?: keyof JSX.IntrinsicElements;
  /** Color del texto: puede ser un color del sistema de diseño o un color CSS personalizado */
  color?: TextColor | (string & {});
  /** @deprecated Usa 'color' en su lugar */
  textColor?: TextColor;
}

export const Body = ({
  size = 'xs',
  weight = 'regular',
  children,
  className,
  as = 'span',
  color,
  textColor,
  ...props
}: BodyProps) => {
  const Component = as;

  // Construir las clases según el tamaño
  const getTypographyClasses = (): string => {
    const classes: string[] = [];

    if (size && ['xs', 'sm', 'md', 'lg'].includes(size)) {
      // Usar clases individuales de Tailwind que están definidas en fontSize, fontWeight, lineHeight, letterSpacing
      classes.push(`ui-text-body-${size}-${weight}`);
      classes.push(`ui-font-body-${size}-${weight}`);
      classes.push(`ui-leading-body-${size}-${weight}`);
      classes.push(`ui-tracking-body-${size}`);
    } else if (size) {
      // Usar valores base (2xs, xs, sm, md, lg, xl, 2xl, etc.)
      classes.push(`ui-text-${size}`);

      // Aplicar weight base
      if (weight !== 'regular') {
        classes.push(`ui-font-${weight}`);
      }
    }

    return classes.join(' ');
  };

  const typographyClasses = getTypographyClasses();

  // Verificar si el color es del sistema de diseño
  const isSystemColor = color && SYSTEM_COLORS.includes(color as TextColor);

  // Estilos inline solo para colores CSS personalizados
  const style = color && !isSystemColor ? { color } : undefined;

  // Color del sistema de diseño o por defecto: secondary
  const getSystemColorClass = (): string => {
    if (isSystemColor) return `ui-text-${color}`;
    if (color) return '';
    if (textColor) return `ui-text-${textColor}`;
    return 'ui-text-secondary'; // Por defecto
  };

  const systemColorClass = getSystemColorClass();

  return (
    <Component
      className={cn('ui-imj', typographyClasses, systemColorClass, className)}
      style={style}
      {...props}
    >
      {children}
    </Component>
  );
};

Body.displayName = 'Body';
